import * as d3 from 'd3';
import PropTypes from 'prop-types';
import React, { useRef, useContext, useEffect } from 'react';
import styled, { ThemeContext } from 'styled-components';
import useResizeObserver from 'use-resize-observer';

const RootContainer = styled.div`
  padding: 0px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const GraphContainer = styled.div`
  display: inline-block;
  margin: 20px auto;
  width: 100%;
`;

const RANGE_MAX = 575;
const TIP_HEIGHT = 40;

function ThresholdGraph({ model, value, thresholdValue }) {
  const d3Container = useRef(null);
  const d3Content = useRef(null);
  const [ref, width] = useResizeObserver();

  const isOverThreshold = value >= thresholdValue;
  const lesser = value >= thresholdValue ? thresholdValue : value;
  const greater = value >= thresholdValue ? value : thresholdValue;
  const context = useContext(ThemeContext);

  useEffect(() => {
    if (width > 1 && value && d3Container.current) {
      const g = d3.select(d3Content.current);
      g.selectAll('*').remove();

      const threshold = d3.scaleThreshold().domain([lesser, greater]);

      const x = d3
        .scaleLinear()
        .domain([0, 1])
        .range([0, width - 20 || RANGE_MAX]);

      const xAxis = d3
        .axisBottom(x)
        .tickSize(0)
        .tickPadding(10)
        .tickValues([0, ...threshold.domain(), 1.0])
        .tickFormat(function (d) {
          if (d === value) return '';
          return d;
        });

      g.call(xAxis);
      g.select('.domain').remove();
      const valueList = [0, ...threshold.domain(), 1.0];
      const lineSegments = valueList
        .map((item, index) => {
          if (index === valueList.length - 1) return [];
          return [valueList[index], valueList[index + 1]];
        })
        .filter(item => item.length);

      const getLineSegment = d => {
        return `M ${x(d[0])} 0 ${x(d[1])} 0`;
      };

      g.selectAll('path')
        .data(lineSegments)
        .enter()
        .insert('path', '.tick')
        .attr('d', getLineSegment)
        .attr('stroke-width', '2')
        .attr('color', context.enigmaGreyLight)
        .attr('stroke', function (d) {
          if (d[1] === value && value > thresholdValue) return context.invalid;
          if (d[0] === value && value < thresholdValue) return context.valid;
          return context.enigmaGreyLight;
        })
        .attr('stroke-dasharray', function (d) {
          if (d[1] === value && value > thresholdValue) return '5,5';
          if (d[0] === value && value < thresholdValue) return '5,5';
          return '';
        });

      // add threshold text
      g.append('text')
        .attr('font-size', '14px') // todo use theme
        .attr('fill', context.enigmaDarkGrey) // todo use theme
        .attr('text-anchor', 'start')
        .attr('x', x(thresholdValue) - 30)
        .attr('y', 40)
        .text('Threshold');

      g.append('path')
        .attr('id', 'foo') // todo use theme
        .attr('stroke', '') // todo use theme
        .attr('fill', isOverThreshold ? context.invalid : context.valid) // todo use theme
        .attr('d', `M 0 0 L 16 0 L 8 16 z`)
        .attr('x', x(value) - 35)
        .attr('y', -80)
        .attr('transform', `translate(${x(value) - 10}, ${TIP_HEIGHT * -0.4})`);

      g.append('text')
        .attr('font-size', '14px') // todo use theme
        .attr('fill', context.enigmaWhite) // todo use theme
        .attr('text-anchor', 'start')
        .attr('x', x(value) - 25)
        .attr('y', -25)
        .attr('font-family', 'monospace')
        .text('Score');

      g.append('text')
        .attr('font-size', '20px') // todo use theme
        .attr('fill', context.enigmaWhite) // todo use theme
        .attr('text-anchor', 'start')
        .attr('x', x(value) - 25)
        .attr('y', -45)
        .attr('font-family', 'monospace')
        .text(`${value.toFixed(2)}`);
    }
  }, [value, width]);

  const Container = RootContainer;

  return (
    <Container ref={ref}>
      <GraphContainer>
        <svg
          className='d3-component'
          width='100%'
          height='150px'
          ref={d3Container}
        >
          <g ref={d3Content} transform='translate(10, 100)'></g>
        </svg>
      </GraphContainer>
    </Container>
  );
}

ThresholdGraph.propTypes = {
  value: PropTypes.number.isRequired,
  thresholdValue: PropTypes.number,
};

ThresholdGraph.defaultProps = {
  thresholdValue: 0.5,
};

export default ThresholdGraph;
